/**
 * Testimonials Section - Landing Page
 * Showcasing client quotes and their applications
 */

import React from 'react';

const Testimonials = ({ language }) => {
  const isArabic = language === 'ar';

  const customers = [
    {
      name: isArabic ? 'ودني - تطبيق الراكب' : 'WDNI - Rider App',
      logo: 'https://firebasestorage.googleapis.com/v0/b/wdni-518ff.firebasestorage.app/o/images%2FIMG-20250518-WA0056_1747597844430.jpg?alt=media&token=c1062fad-3079-495f-be81-55f1c412c916',
      link: 'https://play.google.com/store/apps/details?id=com.wdni.customers&pcampaignid=web_share',
      description: isArabic ? 'تطبيق النقل والتوصيل الموثوق' : 'Reliable ride-sharing app',
    },
    {
      name: isArabic ? 'ودني - تطبيق السائق' : 'WDNI - Driver App',
      logo: 'https://firebasestorage.googleapis.com/v0/b/wdni-518ff.firebasestorage.app/o/images%2FIMG-20250518-WA0056_1747597844430.jpg?alt=media&token=c1062fad-3079-495f-be81-55f1c412c916',
      link: 'https://play.google.com/store/apps/details?id=com.wdni.drivers&pcampaignid=web_share',
      description: isArabic ? 'تطبيق السائقين المتطور' : 'Advanced driver application',
    },
    {
      name: 'وفّر | Waffer',
      logo: 'https://play-lh.googleusercontent.com/4v-WoJBuN4Uz1z878ThE5nghsyGXryURA7VpwnIIflJrTTDIqorUzP4BXXIagybOFHcy172r90R6laBXlvJ6=w480-h960',
      link: 'https://play.google.com/store/apps/details?id=com.syriasave.waffer',
      description: isArabic ? 'تطبيق توفير الأموال والمتاجر' : 'Money-saving deals app',
    },
  ];

  const testimonials = isArabic
    ? [
        {
          name: 'أحمد محمد',
          company: 'تطبيق Wudni',
          role: 'الرئيس التنفيذي',
          quote: 'خدمة ممتازة وموثوقة جداً، توصيل سريع جداً. زادت رضا مستخدمينا بنسبة 95%',
          rating: 5,
        },
        {
          name: 'فاطمة علي',
          company: 'منصة التجارة الإلكترونية',
          role: 'مدير المنتج',
          quote: 'أفضل حل OTP استخدمته، دعم رائع جداً ويساعد في كل احتياجاتنا',
          rating: 5,
        },
        {
          name: 'محمود عبدالله',
          company: 'تطبيق Wudni',
          role: 'مهندس البرمجيات',
          quote: 'السعر مناسب والأداء استثنائي، التكامل سهل جداً مع نظامنا',
          rating: 5,
        },
      ]
    : [
        {
          name: 'Ahmed Mohammed',
          company: 'Wudni App',
          role: 'CEO',
          quote: 'Excellent and highly reliable service. Instant delivery exceeded our expectations. Customer satisfaction increased by 95%',
          rating: 5,
        },
        {
          name: 'Fatima Ali',
          company: 'E-commerce Platform',
          role: 'Product Manager',
          quote: 'Best OTP solution available. Outstanding support that helps with every need. Seamless integration.',
          rating: 5,
        },
        {
          name: 'Mahmoud Abdullah',
          company: 'Wudni App',
          role: 'Software Engineer',
          quote: 'Great pricing with exceptional performance. Integration is straightforward and works perfectly with our system.',
          rating: 5,
        },
      ];

  return (
    <section className="w-full py-16 sm:py-24 md:py-32 lg:py-48 px-3 sm:px-4 bg-[#0B0E14] relative">
      {/* Background accent */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#128C7E]/5 to-transparent pointer-events-none" />
      
      <div className="relative max-w-6xl mx-auto">
        {/* Our Customers Section */}
        <div>
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white text-center mb-4 sm:mb-6">
              {isArabic ? 'عملاؤنا' : 'Our Customers'}
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-[#A0AEC0] max-w-2xl mx-auto leading-relaxed">
              {isArabic 
                ? 'تطبيقات موثوقة تعتمد على خدماتنا' 
                : 'Trusted by leading mobile applications'}
            </p>
          </div>

          {/* Customers Grid - Simple Icon and Name */}
          <div className="flex flex-wrap justify-center items-center gap-8 sm:gap-12 md:gap-16 lg:gap-24">
            {customers.map((customer, index) => (
              <a
                key={index}
                href={customer.link}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex flex-col items-center"
              >
                {/* App Logo Circle */}
                <div className="relative mb-4 sm:mb-6">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#128C7E]/40 to-[#128C7E]/10 rounded-full blur-lg sm:blur-xl group-hover:blur-2xl transition-all" />
                  <div className="relative w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 rounded-full border-2 border-[#128C7E]/50 group-hover:border-[#128C7E] overflow-hidden shadow-lg sm:shadow-xl shadow-[#128C7E]/20 group-hover:shadow-[#128C7E]/40 transition-all bg-white p-2">
                    <img
                      src={customer.logo}
                      alt={customer.name}
                      className="w-full h-full object-cover rounded-full"
                    />
                  </div>
                </div>

                {/* App Name */}
                <h3 className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-white text-center group-hover:text-[#128C7E] transition-colors">
                  {customer.name}
                </h3>
              </a>
            ))}
          </div>

          {/* Mobile Badge */}
          <div className="mt-10 sm:mt-12 md:mt-16 text-center">
            <p className="text-[#A0AEC0] text-xs sm:text-sm">
              {isArabic 
                ? '✓ متاح على متجر جوجل بلاي' 
                : '✓ Available on Google Play Store'}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
